import React from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPES = ['status', 'service_head', 'job_category', 'job_type', 'service_supervisor', 'service_technician', 'customer_type'];

/**
 * Internal: fetches FULL master records (not just names) for every Service
 * master type. Returns { recordsByType, loading, error, refetch }.
 *
 * Auto-refreshes whenever any consumer fires the global `masters:changed`
 * CustomEvent OR a cross-tab BroadcastChannel `masters` message.
 *
 * Not exported — callers use useServiceMasters() or useServiceMastersForFilters().
 */
function useServiceMastersRecords() {
  const [records, setRecords] = React.useState(() => {
    const init = {};
    TYPES.forEach((t) => { init[t] = []; });
    return init;
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const responses = await Promise.all(
        TYPES.map((t) => axios.get(`${API}/masters?type=${t}`).catch(() => ({ data: [] })))
      );
      const next = {};
      TYPES.forEach((t, i) => { next[t] = responses[i].data || []; });
      setRecords(next);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  // Same-tab + cross-tab invalidation
  React.useEffect(() => {
    const onWinEvent = () => fetchAll();
    window.addEventListener('masters:changed', onWinEvent);

    let bc;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel('masters');
        bc.onmessage = (msg) => {
          if (msg?.data?.type === 'masters:changed') fetchAll();
        };
      }
    } catch {
      // older browsers — ignore
    }

    return () => {
      window.removeEventListener('masters:changed', onWinEvent);
      if (bc) {
        try { bc.close(); } catch { /* noop */ }
      }
    };
  }, [fetchAll]);

  return { records, loading, error, refetch: fetchAll };
}

/** Dedupe an array of master records down to a name list (case-sensitive, preserves order). */
function toNameList(records) {
  const names = Array.from(new Set((records || []).map((m) => m.name).filter(Boolean)));
  return names;
}

/** Build the { status:[], service_head:[], … } shape expected by existing consumers. */
function shapeFromRecords(recordsByType, keepRecord) {
  const filtered = {};
  for (const t of TYPES) {
    const list = recordsByType[t] || [];
    filtered[t] = keepRecord ? list.filter(keepRecord) : list;
  }
  const out = {
    status: toNameList(filtered.status),
    service_head: toNameList(filtered.service_head),
    job_category: toNameList(filtered.job_category),
    job_type: toNameList(filtered.job_type),
    customer_type: toNameList(filtered.customer_type),
    // Aliases for ergonomic field-name matching with service_jobs documents
    supervisor: toNameList(filtered.service_supervisor),
    technician: toNameList(filtered.service_technician),
  };
  return out;
}

/**
 * useServiceMasters
 *
 * PUBLIC SHAPE (unchanged): Returns arrays of distinct master names keyed by
 * type, plus `{ loading, error, refetch }`. Every Service master record is
 * included regardless of its `is_filter_visible` flag — this hook is used by
 * the Job Card form, Job Card edit form, Templates, Dashboard, and backfill
 * helpers where admins need to see every master value.
 */
export function useServiceMasters() {
  const { records, loading, error, refetch } = useServiceMastersRecords();
  const shaped = React.useMemo(() => shapeFromRecords(records), [records]);
  return { ...shaped, loading, error, refetch };
}

/**
 * useServiceMastersForFilters
 *
 * Same shape as useServiceMasters() but filters out any record whose
 * `is_filter_visible` flag is explicitly false. Records missing the field are
 * treated as visible (backward compatibility — backend coerces to True on GET).
 *
 * Use this in FILTER / SEARCH dropdowns across the Service module so that
 * admins can hide obsolete or rarely-used master values from filter pickers
 * without deleting them.
 */
export function useServiceMastersForFilters() {
  const { records, loading, error, refetch } = useServiceMastersRecords();
  const shaped = React.useMemo(
    () => shapeFromRecords(records, (m) => m.is_filter_visible !== false),
    [records]
  );
  return { ...shaped, loading, error, refetch };
}

/** Dispatcher used by ServiceMasters.js (and any other writer) to broadcast
 *  that a master row has been created / updated / deleted / synced.
 *  Notifies BOTH same-tab and cross-tab consumers. */
export function broadcastMastersChanged(detail = {}) {
  // Same-tab listeners
  try {
    window.dispatchEvent(new CustomEvent('masters:changed', { detail }));
  } catch {
    // older browsers — ignore
  }
  // Cross-tab listeners (other open tabs of this app)
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const bc = new BroadcastChannel('masters');
      bc.postMessage({ type: 'masters:changed', ...detail });
      bc.close();
    }
  } catch {
    // older browsers — ignore
  }
}

/** Filters a list of literal status names to those that exist in the master.
 *  Used to safely apply default Status pre-selection. */
export function intersectLiteral(masterList, literals) {
  const set = new Set(masterList || []);
  return (literals || []).filter((s) => set.has(s));
}
